import type { TableElement } from '../model/form.types';
import { getMergedCellBounds } from '../lib/cell-merge-algorithm';

interface NavigationResult {
  newRow: number;
  newCol: number;
}

interface CellFootprint {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

function getFootprint(row: number, col: number, table?: TableElement): CellFootprint {
  const mergeGroupId = table?.properties.cells[row]?.[col]?.mergeGroupId;
  if (table && mergeGroupId) {
    const bounds = getMergedCellBounds(mergeGroupId, table);
    if (bounds) {
      return {
        top: bounds.startRow,
        left: bounds.startCol,
        bottom: bounds.endRow,
        right: bounds.endCol,
      };
    }
  }

  return { top: row, left: col, bottom: row, right: col };
}

function resolveToRoot(row: number, col: number, table?: TableElement): NavigationResult {
  const mergeGroupId = table?.properties.cells[row]?.[col]?.mergeGroupId;
  if (table && mergeGroupId) {
    const bounds = getMergedCellBounds(mergeGroupId, table);
    if (bounds) {
      return { newRow: bounds.startRow, newCol: bounds.startCol };
    }
  }

  return { newRow: row, newCol: col };
}

export function useTableKeyboardNavigation() {
  const handleTabNavigation = (
    row: number,
    col: number,
    columns: number,
    rows: number,
    shiftKey: boolean,
    table?: TableElement
  ): NavigationResult => {
    const footprint = getFootprint(row, col, table);
    let newRow = footprint.top;
    let newCol = col;

    if (shiftKey) {
      newCol = footprint.left - 1;
      if (newCol < 0) {
        newCol = columns - 1;
        newRow = Math.max(0, footprint.top - 1);
      }
    } else {
      newCol = footprint.right + 1;
      if (newCol >= columns) {
        newCol = 0;
        newRow = Math.min(rows - 1, footprint.bottom + 1);
      }
    }

    return resolveToRoot(newRow, newCol, table);
  };

  const handleArrowNavigation = (
    key: string,
    row: number,
    col: number,
    rows: number,
    columns: number,
    table?: TableElement
  ): NavigationResult => {
    const footprint = getFootprint(row, col, table);
    let newRow = row;
    let newCol = col;

    switch (key) {
      case 'ArrowUp':
        newRow = Math.max(0, footprint.top - 1);
        break;
      case 'ArrowDown':
        newRow = Math.min(rows - 1, footprint.bottom + 1);
        break;
      case 'ArrowLeft':
        newCol = Math.max(0, footprint.left - 1);
        break;
      case 'ArrowRight':
        newCol = Math.min(columns - 1, footprint.right + 1);
        break;
    }

    return { newRow, newCol };
  };

  return {
    handleTabNavigation,
    handleArrowNavigation,
  };
}
